import {
  AppBar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Typography
} from '@material-ui/core'
import { CircleArrowLeft, EllipsisVertical, Pencil, Trash2 } from 'lucide-react'
import { useContext, useEffect, useState } from 'react'
import { AuthContext } from '../../context/Auth/AuthContext'
import { SocketContext } from '../../context/Socket/SocketContext'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { ButtonPrimary } from '../ButtonPrimary'
import { ButtonSecondary } from '../ButtonSecondary'
import { ChatModal } from '../ChatModal'
import UserAvatar from '../UserAvatar'
import { useStyles } from './style'

const ChatHeader = ({
  user,
  chat,
  onClose,
  isGroupChat,
  chatTitle,
  participantsText,
  handleUpdateChat
}) => {
  const classes = useStyles()
  const { user: currentUser } = useContext(AuthContext)
  const socketManager = useContext(SocketContext)

  const [anchorEl, setAnchorEl] = useState(null)
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  const [avatarUrl, setAvatarUrl] = useState('')

  useEffect(() => {
    let isMounted = true

    const fetchAvatar = async () => {
      if (isGroupChat || !user?.id) {
        if (isMounted) setAvatarUrl('')
        return
      }

      try {
        const { data } = await api.get(`/users/${user.id}/avatar`)
        if (
          isMounted &&
          data &&
          typeof data === 'string' &&
          !data.includes('default-avatar.png')
        ) {
          setAvatarUrl(`${data}?cb=${Date.now()}`)
        } else if (isMounted) {
          setAvatarUrl('')
        }
      } catch (err) {
        console.warn('Falha ao buscar avatar no header')
        if (isMounted) setAvatarUrl('')
      }
    }

    fetchAvatar()

    return () => {
      isMounted = false
    }
  }, [isGroupChat, user?.id])

  const handleOpenMenu = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const handleEditGroup = () => {
    setIsEditModalOpen(true)
    handleCloseMenu()
  }

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false)
  }

  const handleShowDeleteConfirm = () => {
    setIsConfirmDeleteOpen(true)
    handleCloseMenu()
  }

  const handleCancelDelete = () => {
    setIsConfirmDeleteOpen(false)
  }

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/chats/${chat.id}`)
      const companyId = localStorage.getItem('companyId')
      const socket = socketManager.GetSocket(companyId)

      if (socket) {
        socket.emit(`company-${companyId}-chat`, {
          action: 'delete',
          id: chat.id
        })
      }
      onClose()
    } catch (err) {
      toastError(err)
    } finally {
      setIsConfirmDeleteOpen(false)
    }
  }

  const displayName =
    chatTitle || (isGroupChat ? chat?.title : user?.name) || ''

  return (
    <>
      <AppBar position="sticky" className={classes.header}>
        <div className={classes.headerContent}>
          <IconButton
            className={classes.backButton}
            onClick={onClose}
            edge="start"
          >
            <CircleArrowLeft />
          </IconButton>

          <UserAvatar
            name={displayName}
            src={avatarUrl}
            isGroup={isGroupChat}
            className={classes.avatar}
          />

          <div className={classes.titleContainer}>
            <Typography className={classes.userName}>{chatTitle}</Typography>

            {isGroupChat && participantsText && (
              <Typography
                variant="caption"
                className={classes.participantsList}
              >
                {participantsText}
              </Typography>
            )}
          </div>

          {isGroupChat && currentUser.profile === 'admin' && (
            <IconButton
              className={classes.menuButton}
              onClick={handleOpenMenu}
              aria-label="opções do grupo"
            >
              <EllipsisVertical />
            </IconButton>
          )}

          <Menu
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
          >
            <MenuItem onClick={handleEditGroup} className={classes.menuItem}>
              <Pencil size={16} />
              Editar
            </MenuItem>
            <MenuItem
              onClick={handleShowDeleteConfirm}
              className={classes.menuItem}
            >
              <Trash2 size={16} />
              Excluir
            </MenuItem>
          </Menu>
        </div>
      </AppBar>

      <Dialog
        open={isConfirmDeleteOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="xs"
      >
        <DialogTitle id="alert-dialog-title" className={classes.dialogTitle}>
          {'Confirmar exclusão'}
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText id="alert-dialog-description">
            Tem certeza que deseja excluir este grupo? Esta ação não pode ser
            desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <ButtonSecondary onClick={handleCancelDelete} label="Cancelar" />

          <ButtonPrimary
            onClick={handleConfirmDelete}
            label={'Excluir'}
            color="primary"
          />
        </DialogActions>
      </Dialog>

      {/* Modal de edição de grupo */}
      {isEditModalOpen && (
        <ChatModal
          open={isEditModalOpen}
          chat={chat}
          type="edit"
          handleClose={handleCloseEditModal}
          handleLoadNewChat={handleUpdateChat}
          user={currentUser}
        />
      )}
    </>
  )
}

export default ChatHeader
